import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Define the interface for the user object
interface IUserProps {
	accountId?: number;
	accountIdPrefix?: string;
	closeOnly?: boolean;
	creditLoanPercentage?: number;
	currencyPolicyId?: number;
	currencySign?: string;
	demo?: boolean;
	enableCashDelivery?: boolean;
	firstName?: string;
	genericPolicyId?: number;
	id?: number;
	lastName?: string;
	locked?: boolean;
	parentId?: number;
	percentageLevel1?: number;
	percentageLevel2?: number;
	percentageLevel3?: number;
	percentageLevel4?: number;
	tradingType?: number;
	userCurrencyId?: number;
	userName?: string;
	userType?: number;
	whiteLabel?: boolean;
}
interface ICurrencyPolicyProps {
	requiredMarginTypeId?: number;
	openCommissionTypeId?: number;
	closedCommissionTypeId?: number;
	intraDayCommissionValue?: number;
	requiredMargin?: number;
	sellRequiredMarginType?: number;
	sellRequiredMargin?: number;
	buyHoldingMarginType?: number;
	buyHoldingMargin?: number;
	sellHoldingMarginType?: number;
	sellHoldingMargin?: number;
	spreadBalance?: number;
	spread?: number;
	entryAutoBroker?: number;
	limitOffset?: number;
	marketAutoBroker?: number;
	maximumAmountPerDeal?: number;
	maximumQuantity?: number;
	maximumValue?: number;
	percentage?: number;
	stopOffset?: number;
	intraDayCommissionTypeId?: number;
	sellOnly?: boolean;
	buyOnly?: boolean;
	limitOffsetTypeId?: number;
	stopOffsetTypeId?: number;
	minimumAmountPerDeal?: number;
	currencyId?: number;
	closedCommissionValue?: number;
	openCommissionValue?: number;
	visible?: boolean;
	spreadTypeId?: number;
}

// Define the database schema
interface MyDB extends DBSchema {
	users: {
		key: number;
		value: IUserProps;
	};
	selectedColumns: {
		key: string;
		value: { name: string; columns: string[] };
	};
	currencyPolicies: {
		key: number;
		value: ICurrencyPolicyProps;
	};
}

// Function to open the IndexedDB database
export const openDatabase = async (): Promise<IDBPDatabase<MyDB>> => {
	return openDB<MyDB>('ark_database', 3, {
		upgrade(db, oldVersion, newVersion, transaction) {
			// Check and create 'users' object store if it doesn't exist
			if (!db.objectStoreNames.contains('users')) {
				db.createObjectStore('users', { keyPath: 'id' });
			}

			// Check and create 'selectedColumns' object store if it doesn't exist
			if (!db.objectStoreNames.contains('selectedColumns')) {
				db.createObjectStore('selectedColumns', { keyPath: 'name' });
			}

			// Check and create 'currencyPolicies' object store if it doesn't exist
			if (!db.objectStoreNames.contains('currencyPolicies')) {
				db.createObjectStore('currencyPolicies', { keyPath: 'currencyId' });
			}
		},
	}).catch((error) => {
		console.error('Error opening database:', error);
		throw error;
	});
};

// Function to store users in the database
export const storeUsers = async (db: IDBPDatabase<MyDB> | null, users: IUserProps[]) => {
	if (!db) {
		return { success: false, error: 'No database connection' };
	}

	try {
		// Start a readwrite transaction on the 'users' store
		const tx = db.transaction('users', 'readwrite');
		const store = tx.objectStore('users');

		// Clear existing data (if necessary, consider updating instead)
		await store.clear();

		// Batch add users
		for (const user of users) {
			await store.put(user); // Insert or update user data
		}

		// Ensure the transaction completes
		await tx.done;

		return { success: true };
	} catch (error) {
		console.error('Error storing users in the database:', error);
		return { success: false, error };
	}
};

// function to store currency policies in the database
export const storeCurrencyPolicies = async (
	db: IDBPDatabase<MyDB>,
	currencyPolicies: ICurrencyPolicyProps[],
) => {
	if (db) {
		const tx = db.transaction('currencyPolicies', 'readwrite');
		const store = tx.objectStore('currencyPolicies');
		await store.clear();
		for (const currencyPolicy of currencyPolicies) {
			await store.put(currencyPolicy);
		}
		await tx.done;
	}
};

// Function to get all users from the database
export const getAllUsers = async (db: IDBPDatabase<MyDB>) => {
	if (db) {
		const tx = db.transaction('users', 'readonly');
		const store = tx.objectStore('users');
		return store.getAll();
	}
	return [];
};

// function to get all currency policies from the database
export const getAllCurrencyPolicies = async (db: IDBPDatabase<MyDB>) => {
	if (db) {
		const tx = db.transaction('currencyPolicies', 'readonly');
		const store = tx.objectStore('currencyPolicies');
		return store.getAll();
	}
	return [];
};
// export const getUsersHMap = async (db: IDBPDatabase<MyDB>) => {
//   if (db) {
//     const tx = db.transaction('users', 'readonly');
//     const store = tx.objectStore('users');
//     const users = await store.getAll();
//     const usersMap = new Map();
//     for (const user of users) {
//       usersMap.set(user.id, user);
//     }
//     return usersMap;
//   }
//   return new Map();
// }
export const removeCurrencyPolicies = async (db: IDBPDatabase<MyDB>) => {
	if (db) {
		const tx = db.transaction('currencyPolicies', 'readwrite');
		const store = tx.objectStore('currencyPolicies');
		await store.clear();
		await tx.done;
	}
};

// Function to remove all users from the database
export const removeAllUsers = async (db: IDBPDatabase<MyDB>) => {
	if (db) {
		const tx = db.transaction('users', 'readwrite');
		const store = tx.objectStore('users');
		await store.clear();
		await tx.done;
	}
};

export const storeSelectedColumns = async (
	db: IDBPDatabase<MyDB>,
	columns: string[],
	name: string,
) => {
	try {
		if (db) {
			const tx = db.transaction('selectedColumns', 'readwrite');
			const store = tx.objectStore('selectedColumns');

			// Store columns with `name` as part of the object
			const dataToStore = { name, columns };
			await store.put(dataToStore);
			await tx.done;
		}
	} catch (e) {
		console.log(e);
	}
};

export const getSelectedColumns = async (db: IDBPDatabase<MyDB>) => {
	if (db) {
		const tx = db.transaction('selectedColumns', 'readonly');
		const store = tx.objectStore('selectedColumns');
		return store.getAll();
	}
	return [];
};

